// surveyMachine.ts
// Core survey state machine using XState v5

import { setup, assign, createActor } from 'xstate';
import type { SurveyContext, SurveyEvent, GuardParams } from './surveyMachine.types';

/**
 * Survey state machine
 * This machine is the single source of truth for survey flow logic
 * All navigation, conditions, and state transitions are defined here
 */
export const surveyMachine = setup({
  types: {} as {
    context: SurveyContext;
    events: SurveyEvent;
  },

  // Guards: Boolean conditions that determine which transition to take
  guards: {
    /**
     * Path selection guards based on Q1 answer
     */
    selectedPathA: ({ context }) => {
      const q1Answer = context.answers['Q1'];
      return q1Answer === 'A' || q1Answer === "I'm working in Portugal (Portuguese national)" ||
             q1Answer === "I'm working in Portugal (immigrant/expat, not Portuguese)";
    },

    selectedPathB: ({ context }) => {
      const q1Answer = context.answers['Q1'];
      return q1Answer === 'B' || q1Answer === "I'm unemployed and job searching in Portugal";
    },

    selectedPathC: ({ context }) => {
      const q1Answer = context.answers['Q1'];
      return q1Answer === 'C' || q1Answer === "I used to live/work in Portugal, but now I'm abroad";
    },

    selectedPathD: ({ context }) => {
      const q1Answer = context.answers['Q1'];
      return q1Answer === 'D' || q1Answer === "Living in Portugal but not working";
    },

    /**
     * Conditional guards for sub-questions and branching
     */
    answeredYes: ({ context }, params: { questionId: string }) => {
      return context.answers[params.questionId]?.toLowerCase() === 'yes';
    },

    answeredNo: ({ context }, params: { questionId: string }) => {
      return context.answers[params.questionId]?.toLowerCase() === 'no';
    },

    selectedOption: ({ context }, params: { questionId: string; value: string }) => {
      return context.answers[params.questionId] === params.value;
    },

    /**
     * Scale-based conditions (for questions like "Did you select Salary?")
     */
    includesValue: ({ context }, params: { questionId: string; value: string }) => {
      const answer = context.answers[params.questionId];
      if (Array.isArray(answer)) {
        return answer.includes(params.value);
      }
      return answer === params.value;
    },

    scaleCondition: ({ context }, params: { questionId: string; operator: '<=' | '>=' | '<' | '>' | '='; threshold: number }) => {
      const value = context.answers[params.questionId];
      if (typeof value !== 'number') return false;

      switch (params.operator) {
        case '<=': return value <= params.threshold;
        case '>=': return value >= params.threshold;
        case '<': return value < params.threshold;
        case '>': return value > params.threshold;
        case '=': return value === params.threshold;
        default: return false;
      }
    },

    /**
     * Validation guards
     */
    hasAnswer: ({ context }, params: { questionId: string }) => {
      const answer = context.answers[params.questionId];
      return answer !== undefined && answer !== null && answer !== '';
    },
  },

  // Actions: Side effects and state updates
  actions: {
    saveAnswer: assign(({ context, event }) => {
      if (event.type !== 'ANSWER') return context;

      return {
        ...context,
        answers: {
          ...context.answers,
          [event.questionId]: event.value,
        },
      };
    }),

    setPathA: assign({
      currentPath: 'A' as const,
    }),

    setPathB: assign({
      currentPath: 'B' as const,
    }),

    setPathC: assign({
      currentPath: 'C' as const,
    }),

    setPathD: assign({
      currentPath: 'D' as const,
    }),

    updateBlock: assign(({ context }, params: { block: number | 'final' }) => ({
      ...context,
      currentBlock: params.block,
    })),

    // Note: History is now tracked in the adapter, not in the machine
    // This keeps the machine simpler and focused on state logic

    goBack: assign(({ context }) => {
      if (context.history.length === 0) return context;

      const newHistory = [...context.history];
      newHistory.pop(); // Remove last state

      return {
        ...context,
        history: newHistory,
      };
    }),

    updateProgress: assign(({ context }) => {
      const totalBlocks = 4; // Blocks 0, 1, 2, 3 + final
      const currentBlockNum = typeof context.currentBlock === 'number' ? context.currentBlock : 4;

      return {
        ...context,
        progress: {
          current: currentBlockNum + 1,
          total: totalBlocks,
          percentage: Math.round(((currentBlockNum + 1) / totalBlocks) * 100),
        },
      };
    }),

    saveEmail: assign(({ context, event }) => {
      if (event.type !== 'ANSWER') return context;

      return {
        ...context,
        email: event.value,
      };
    }),
  },
}).createMachine({
  id: 'survey',

  initial: 'Q1',

  context: {
    answers: {},
    currentPath: null,
    currentBlock: 0,
    history: [],
    progress: {
      current: 1,
      total: 4,
      percentage: 25,
    },
  },

  states: {
    /**
     * BLOCK 0: SCREENING & INCLUSION
     */
    Q1: {
      on: {
        ANSWER: {
          target: 'Q2_Industry',
          actions: 'saveAnswer',
        },
      },
    },

    Q2_Industry: {
      on: {
        ANSWER: [
          {
            guard: 'selectedPathA',
            target: 'PathA_Start',
            actions: ['saveAnswer', 'setPathA'],
          },
          {
            guard: 'selectedPathB',
            target: 'PathB_Start',
            actions: ['saveAnswer', 'setPathB'],
          },
          {
            guard: 'selectedPathC',
            target: 'PathC_Start',
            actions: ['saveAnswer', 'setPathC'],
          },
          {
            guard: 'selectedPathD',
            target: 'PathD_Start',
            actions: ['saveAnswer', 'setPathD'],
          },
        ],
        BACK: {
          target: 'Q1',
          actions: 'goBack',
        },
      },
    },

    /**
     * Path markers (transitional states)
     */
    PathA_Start: {
      always: {
        target: 'Q2A',
        actions: { type: 'updateBlock', params: { block: 1 } },
      },
    },

    PathB_Start: {
      always: {
        target: 'Q2B',
        actions: { type: 'updateBlock', params: { block: 1 } },
      },
    },

    PathC_Start: {
      always: {
        target: 'Q2C',
        actions: { type: 'updateBlock', params: { block: 1 } },
      },
    },

    PathD_Start: {
      always: {
        target: 'Q2D',
        actions: { type: 'updateBlock', params: { block: 1 } },
      },
    },

    /**
     * BLOCK 1: WORK CONDITIONS - Path A
     */
    Q2A: {
      on: {
        ANSWER: {
          target: 'Q3A',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2_Industry',
          actions: 'goBack',
        },
      },
    },

    Q3A: {
      on: {
        ANSWER: {
          target: 'Q3A_Alt',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2A',
          actions: 'goBack',
        },
      },
    },

    Q3A_Alt: {
      on: {
        ANSWER: {
          target: 'Q4A_Hours',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q3A',
          actions: 'goBack',
        },
      },
    },

    Q4A_Hours: {
      on: {
        ANSWER: {
          target: 'Q5A',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 2 } }],
        },
        BACK: {
          target: 'Q3A_Alt',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 1: WORK CONDITIONS - Path B
     */
    Q2B: {
      on: {
        ANSWER: {
          target: 'Q3B',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2_Industry',
          actions: 'goBack',
        },
      },
    },

    Q3B: {
      on: {
        ANSWER: {
          target: 'Q4B',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2B',
          actions: 'goBack',
        },
      },
    },

    Q4B: {
      on: {
        ANSWER: {
          target: 'Q5B',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q3B',
          actions: 'goBack',
        },
      },
    },

    Q5B: {
      on: {
        ANSWER: {
          target: 'Q6B',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 2 } }],
        },
        BACK: {
          target: 'Q4B',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 1: WORK CONDITIONS - Path C
     */
    Q2C: {
      on: {
        ANSWER: {
          target: 'Q3C',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2_Industry',
          actions: 'goBack',
        },
      },
    },

    Q3C: {
      on: {
        ANSWER: {
          target: 'Q4C',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2C',
          actions: 'goBack',
        },
      },
    },

    Q4C: {
      on: {
        ANSWER: {
          target: 'Q5C',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q3C',
          actions: 'goBack',
        },
      },
    },

    Q5C: {
      on: {
        ANSWER: {
          target: 'Q6C',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 2 } }],
        },
        BACK: {
          target: 'Q4C',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 1: WORK CONDITIONS - Path D
     */
    Q2D: {
      on: {
        ANSWER: [
          {
            guard: { type: 'selectedOption', params: { questionId: 'Q2D', value: 'Yes, in the past' } },
            target: 'Q3D_A',
            actions: ['saveAnswer'],
          },
          {
            guard: { type: 'selectedOption', params: { questionId: 'Q2D', value: 'No, not yet' } },
            target: 'Q3D_B',
            actions: ['saveAnswer'],
          },
        ],
        BACK: {
          target: 'Q2_Industry',
          actions: 'goBack',
        },
      },
    },

    Q3D_A: {
      on: {
        ANSWER: {
          target: 'Q4D',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2D',
          actions: 'goBack',
        },
      },
    },

    Q3D_B: {
      on: {
        ANSWER: {
          target: 'Q4D',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q2D',
          actions: 'goBack',
        },
      },
    },

    Q4D: {
      on: {
        ANSWER: {
          target: 'Q5D',
          actions: ['saveAnswer'],
        },
        BACK: [
          {
            guard: { type: 'hasAnswer', params: { questionId: 'Q3D_A' } },
            target: 'Q3D_A',
            actions: 'goBack',
          },
          {
            guard: { type: 'hasAnswer', params: { questionId: 'Q3D_B' } },
            target: 'Q3D_B',
            actions: 'goBack',
          },
        ],
      },
    },

    Q5D: {
      on: {
        ANSWER: {
          target: 'Q6D',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 2 } }],
        },
        BACK: {
          target: 'Q4D',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 2: CULTURE & CONTROL - Path A
     */
    Q5A: {
      on: {
        ANSWER: {
          target: 'Q6A',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q4A_Hours',
          actions: 'goBack',
        },
      },
    },

    Q6A: {
      on: {
        ANSWER: {
          target: 'Q7A_Matters',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 3 } }],
        },
        BACK: {
          target: 'Q5A',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 2: CULTURE & CONTROL - Path B
     */
    Q6B: {
      on: {
        ANSWER: {
          target: 'Q7B',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q5B',
          actions: 'goBack',
        },
      },
    },

    Q7B: {
      on: {
        ANSWER: {
          target: 'Q8B',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q6B',
          actions: 'goBack',
        },
      },
    },

    Q8B: {
      on: {
        ANSWER: {
          target: 'Q9B',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q7B',
          actions: 'goBack',
        },
      },
    },

    Q9B: {
      on: {
        ANSWER: {
          target: 'Final_Questions',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 'final' } }],
        },
        BACK: {
          target: 'Q8B',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 2: CULTURE & CONTROL - Path C
     */
    Q6C: {
      on: {
        ANSWER: {
          target: 'Q7C',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q5C',
          actions: 'goBack',
        },
      },
    },

    Q7C: {
      on: {
        ANSWER: {
          target: 'Q8C',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q6C',
          actions: 'goBack',
        },
      },
    },

    Q8C: {
      on: {
        ANSWER: {
          target: 'Final_Questions',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 'final' } }],
        },
        BACK: {
          target: 'Q7C',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 2: CULTURE & CONTROL - Path D
     */
    Q6D: {
      on: {
        ANSWER: {
          target: 'Final_Questions',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 'final' } }],
        },
        BACK: {
          target: 'Q5D',
          actions: 'goBack',
        },
      },
    },

    /**
     * BLOCK 3: PAY & PURPOSE - Path A only
     */
    Q7A_Matters: {
      on: {
        ANSWER: [
          {
            guard: { type: 'includesValue', params: { questionId: 'Q7A_Matters', value: 'Salary' } },
            target: 'Q8A_Paid',
            actions: ['saveAnswer'],
          },
          {
            target: 'Q9A_Eating',
            actions: ['saveAnswer'],
          },
        ],
        BACK: {
          target: 'Q6A',
          actions: 'goBack',
        },
      },
    },

    Q8A_Paid: {
      on: {
        ANSWER: {
          target: 'Q9A_Eating',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q7A_Matters',
          actions: 'goBack',
        },
      },
    },

    Q9A_Eating: {
      on: {
        ANSWER: {
          target: 'Q10A_Like',
          actions: ['saveAnswer'],
        },
        BACK: [
          {
            guard: { type: 'hasAnswer', params: { questionId: 'Q8A_Paid' } },
            target: 'Q8A_Paid',
            actions: 'goBack',
          },
          {
            target: 'Q7A_Matters',
            actions: 'goBack',
          },
        ],
      },
    },

    Q10A_Like: {
      on: {
        ANSWER: {
          target: 'Q11A_Freedom',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q9A_Eating',
          actions: 'goBack',
        },
      },
    },

    Q11A_Freedom: {
      on: {
        ANSWER: {
          target: 'Q12A_Quit',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q10A_Like',
          actions: 'goBack',
        },
      },
    },

    Q12A_Quit: {
      on: {
        ANSWER: {
          target: 'Q13A_Career_Build',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q11A_Freedom',
          actions: 'goBack',
        },
      },
    },

    Q13A_Career_Build: {
      on: {
        ANSWER: {
          target: 'Q14A_Job_Word',
          actions: ['saveAnswer'],
        },
        BACK: {
          target: 'Q12A_Quit',
          actions: 'goBack',
        },
      },
    },

    Q14A_Job_Word: {
      on: {
        ANSWER: {
          target: 'Final_Questions',
          actions: ['saveAnswer', { type: 'updateBlock', params: { block: 'final' } }],
        },
        BACK: {
          target: 'Q13A_Career_Build',
          actions: 'goBack',
        },
      },
    },

    /**
     * FINAL QUESTIONS
     */
    Final_Questions: {
      on: {
        ANSWER: {
          target: 'Final_Email',
          actions: ['saveAnswer'],
        },
        SKIP: {
          target: 'Final_Email',
          actions: 'addToHistory',
        },
        BACK: [
          // Path A returns to Q14A_Job_Word
          {
            guard: { type: 'selectedPathA' },
            target: 'Q14A_Job_Word',
            actions: 'goBack',
          },
          // Path B returns to Q9B
          {
            guard: { type: 'selectedPathB' },
            target: 'Q9B',
            actions: 'goBack',
          },
          // Path C returns to Q8C
          {
            guard: { type: 'selectedPathC' },
            target: 'Q8C',
            actions: 'goBack',
          },
          // Path D returns to Q6D
          {
            guard: { type: 'selectedPathD' },
            target: 'Q6D',
            actions: 'goBack',
          },
        ],
      },
    },

    Final_Email: {
      on: {
        ANSWER: {
          target: 'Complete',
          actions: ['saveEmail'],
        },
        SKIP: {
          target: 'Complete',
          actions: 'addToHistory',
        },
        BACK: {
          target: 'Final_Questions',
          actions: 'goBack',
        },
      },
    },

    /**
     * COMPLETION STATE
     */
    Complete: {
      type: 'final',
      entry: 'updateProgress',
    },
  },
});

/**
 * Create and export actor for testing
 */
export const createSurveyActor = () => createActor(surveyMachine);
